import {useEffect, useState, useRef, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTSVG} from '../../../_gori/helpers'
import {sanitizeHTML} from '../blog/utils/sanitize'
import {formatDate, getMonthYear} from '../blog/utils/dateFormatter'
import {ReleaseModal} from './ReleaseModal'

type Release = {
  id: number
  title: string
  slug: string
  link: string
  featured_image: string | null
  excerpt?: string
  date?: string
  categories?: Array<{id: number; name: string; slug: string}>
}

type ReleasesResponse = {
  releases: Release[]
  total_pages: number
  current_page: number
  per_page: number
  total_releases: number
}

const MAX_VISIBLE_PAGES = 7
const API_URL = process.env.REACT_APP_API_URL || ''

export function ReleasesPage() {
  const intl = useIntl()
  const [releases, setReleases] = useState<Release[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [yearFilter, setYearFilter] = useState('all')
  const [availableYears, setAvailableYears] = useState<string[]>([])
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const releasesRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await fetch(`${API_URL}/api/releases/years`)
        if (res.ok) {
          const data = await res.json()
          const years = data.years
            .map((y: any) => y.slug)
            .sort()
            .reverse()
          setAvailableYears(years)
        }
      } catch (err) {
        console.error('Failed to fetch years:', err)
      }
    }
    fetchYears()
  }, [])

  const fetchReleases = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()
    setLoading(true)
    setError(null)

    try {
      let url = `${API_URL}/api/releases/list?per_page=10&page=${page}`

      if (yearFilter !== 'all') {
        url += `&year=${yearFilter}`
      }

      const res = await fetch(url, {signal: abortControllerRef.current.signal})

      if (!res.ok) throw new Error('Failed to fetch releases')

      const data: ReleasesResponse = await res.json()

      setReleases(data.releases || [])
      setTotalPages(data.total_pages || 1)

      if (releasesRef.current && page > 1) {
        releasesRef.current.scrollIntoView({behavior: 'smooth', block: 'start'})
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return

      console.error('Error fetching releases:', err)
      setError(err instanceof Error ? err.message : 'Failed to load releases')
    } finally {
      setLoading(false)
    }
  }, [page, yearFilter])

  useEffect(() => {
    fetchReleases()

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchReleases])

  useEffect(() => {
    setPage(1) // Reset to page 1 when year filter changes
  }, [yearFilter])

  const pageNumbers = useCallback(() => {
    if (totalPages <= MAX_VISIBLE_PAGES) {
      return Array.from({length: totalPages}, (_, i) => i + 1)
    }

    const pages: (number | string)[] = [1]
    const leftSiblingIndex = Math.max(page - 1, 2)
    const rightSiblingIndex = Math.min(page + 1, totalPages - 1)

    if (leftSiblingIndex > 2) pages.push('...')

    for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
      pages.push(i)
    }

    if (rightSiblingIndex < totalPages - 1) pages.push('...')
    if (totalPages > 1) pages.push(totalPages)

    return pages
  }, [page, totalPages])()

  const groupedReleases = releases.reduce((groups, release) => {
    if (!release.date) return groups
    const monthYear = getMonthYear(release.date)
    if (!groups[monthYear]) {
      groups[monthYear] = []
    }
    groups[monthYear].push(release)
    return groups
  }, {} as Record<string, Release[]>)

  if (loading && releases.length === 0) {
    return (
      <div className='releases-loading'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (error && releases.length === 0) {
    return (
      <div className='alert alert-danger text-center' role='alert'>
        {error}
      </div>
    )
  }

  return (
    <>
      <div className='releases-title-wrapper' ref={releasesRef}>
        <div className='d-flex justify-content-between align-items-center'>
          <h3 className='releases-title mb-0'>
            {intl.formatMessage({id: 'ALL_UPDATES_RELEASES'})}
          </h3>
          <select
            className='form-select releases-year-filter'
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
          >
            <option value='all'>All</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className='releases-list' style={{position: 'relative'}}>
        {loading && releases.length > 0 && (
          <div className='releases-loading-overlay'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        )}
        {Object.entries(groupedReleases).map(([monthYear, monthReleases]) => (
          <div key={monthYear} className='releases-month-group'>
            <h4 className='releases-month-header'>{monthYear}</h4>
            {monthReleases.map((release) => (
              <button
                key={release.slug}
                onClick={() => setSelectedSlug(release.slug)}
                className='release-card card hover-elevate-up'
                aria-label={`Read release: ${sanitizeHTML(release.title, false)}`}
              >
                <div className='row g-0'>
                  <div
                    className={release.featured_image ? 'col-md-7 order-2 order-md-1' : 'col-12'}
                  >
                    <div className='release-card-body card-body'>
                      <div className='d-flex flex-column flex-md-row align-items-md-center release-card-meta'>
                        {release.categories && release.categories.length > 0 && (
                          <div className='release-card-categories'>
                            {release.categories.map((cat) => {
                              const slug = cat.slug.toLowerCase()
                              const badgeClass =
                                slug === 'new-release'
                                  ? 'badge-new-release'
                                  : slug === 'feature-upgrade'
                                  ? 'badge-feature-upgrade'
                                  : 'bg-primary'
                              return (
                                <span key={cat.id} className={`badge ${badgeClass} me-2`}>
                                  {cat.name}
                                </span>
                              )
                            })}
                          </div>
                        )}
                        {release.date && (
                          <span className='release-card-date'>{formatDate(release.date)}</span>
                        )}
                      </div>
                      <h5 className='release-card-title card-title fw-bold'>
                        {sanitizeHTML(release.title, false)}
                      </h5>
                      {release.excerpt && (
                        <div
                          className='release-card-excerpt'
                          dangerouslySetInnerHTML={{__html: sanitizeHTML(release.excerpt)}}
                        />
                      )}
                    </div>
                  </div>
                  {release.featured_image && (
                    <div className='col-md-5 order-1 order-md-2'>
                      <img
                        src={release.featured_image}
                        className='release-card-image'
                        alt={release.title}
                        loading='lazy'
                      />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <nav
          className='d-flex justify-content-center releases-pagination'
          aria-label='Releases pagination'
        >
          <ul className='pagination'>
            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => setPage(1)}
                disabled={page === 1}
                aria-label='First page'
              >
                <KTSVG path='/media/gori/left-double-chevron.svg' className='svg-icon-3' />
              </button>
            </li>
            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                aria-label='Previous page'
              >
                {intl.formatMessage({id: 'PREVIOUS'})}
              </button>
            </li>
            {pageNumbers.map((pageNum, idx) =>
              pageNum === '...' ? (
                <li key={`ellipsis-${idx}`} className='page-item disabled'>
                  <span className='page-link'>...</span>
                </li>
              ) : (
                <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                  <button
                    className='page-link'
                    onClick={() => setPage(pageNum as number)}
                    aria-label={`Page ${pageNum}`}
                    aria-current={page === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                </li>
              )
            )}
            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                aria-label='Next page'
              >
                {intl.formatMessage({id: 'NEXT'})}
              </button>
            </li>
            <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
              <button
                className='page-link'
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                aria-label='Last page'
              >
                <KTSVG path='/media/gori/right-double-chevron.svg' className='svg-icon-3' />
              </button>
            </li>
          </ul>
        </nav>
      )}

      <ReleaseModal slug={selectedSlug} onClose={() => setSelectedSlug(null)} />
    </>
  )
}
